/**
 * Stat — Tier 0 slice-local primitive (SCRUM-167 epic).
 *
 * Single cell of a hero stat strip: uppercase eyebrow label + large
 * value. Used in Property detail hero (Units / Spaces / Photos /
 * Bookable) and Unit detail hero (WD base / WE base / WD floor / WE
 * floor). Per `~/Desktop/design_handoff_properties/source/properties.jsx`
 * line 94.
 *
 * `border` adds a left border for stats following the first in a
 * horizontal row (the visual divider between cells). The border is
 * applied via a Tailwind utility (`border-l ...`) so the parent can
 * override it at a specific breakpoint by passing `className` —
 * e.g. mobile 2×2 grids suppress the third cell's left border
 * because that cell starts a new row (SCRUM-182 P6 Phase B fix).
 */

import * as React from 'react';

export interface StatProps {
  readonly label: string;
  readonly value: React.ReactNode;
  /** Adds a left border for stats following the first one in a row. */
  readonly border?: boolean;
  /**
   * Additional classes (typically responsive border overrides like
   * `!border-l-0 sm:!border-l`). Appended last so callers can override
   * the default border behavior at specific breakpoints.
   */
  readonly className?: string;
}

export function Stat({
  label,
  value,
  border = false,
  className,
}: StatProps): React.ReactElement {
  const classes = [
    'px-6 pt-4 pb-5',
    border ? 'border-l border-[color:var(--border-subtle)]' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={classes}>
      <div
        className="text-xs font-bold uppercase mb-1.5"
        style={{
          color: 'var(--fg-tertiary)',
          letterSpacing: '0.07em',
        }}
      >
        {label}
      </div>
      <div
        className="text-2xl font-semibold leading-tight"
        style={{
          color: 'var(--fg-primary)',
          letterSpacing: '-0.025em',
        }}
      >
        {value}
      </div>
    </div>
  );
}
